var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useStateWithMutators } from '@rad/hooks';
import { formatPhone } from '@rad/utility/formatting';
import { SITE } from '../../../utility/constants';
import { formatInvoiceNumber } from '../../../utility/formatters';
import { postObjectJson } from '../../../utility/request';
import { fetchInspectionsByInvoice } from '../../../http/inspections';
import CModal from '../modal';
import CErrors from '../errors';
import CSelect from '../select';
import CSwitch from '../switch';
import ContactPicker from '../contact-picker';
export const EstimateSendForm = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const [state, mutators] = useStateWithMutators({
        'which_external': 'phone',
        'errors': [],
        'recipient': '',
    });
    const [inspections, setInspections] = React.useState([]);
    const [includeInspections, setIncludeInspections] = React.useState(false);
    React.useEffect(() => {
        if (!props.invoice) {
            setInspections([]);
            setIncludeInspections(false);
            return;
        }
        fetchInspectionsByInvoice(props.invoice.id, {
            'success': (xs) => {
                setInspections(xs);
                setIncludeInspections(xs.length > 0);
            },
            'error': () => {
                setInspections([]);
                setIncludeInspections(false);
            },
        });
    }, [(_a = props.invoice) === null || _a === void 0 ? void 0 : _a.id]);
    const reifyRecipient = React.useCallback((invoice, whichExternal) => {
        var _a, _b, _c;
        if (!invoice) {
            return '';
        }
        switch (whichExternal) {
            case 'phone':
                {
                    const mobile = (_a = invoice.client.contacts.find((x) => x.kind === 'phone' && x.subkind === 'mobile')) === null || _a === void 0 ? void 0 : _a.value;
                    const unknown = (_b = invoice.client.contacts.find((x) => x.kind === 'phone' && x.subkind === 'unknown')) === null || _b === void 0 ? void 0 : _b.value;
                    return formatPhone(mobile || unknown || '');
                }
            case 'email':
                return ((_c = invoice.client.contacts.find((x) => x.kind === 'email')) === null || _c === void 0 ? void 0 : _c.value) || '';
            default:
                return '';
        }
    }, []);
    React.useEffect(() => {
        mutators.which_external('phone');
        mutators.errors([]);
        mutators.recipient(reifyRecipient(props.invoice, 'phone'));
    }, [(_b = props.invoice) === null || _b === void 0 ? void 0 : _b.id, props.sendKind]);
    React.useEffect(() => {
        mutators.recipient(reifyRecipient(props.invoice, state.which_external));
    }, [state.which_external, (_c = props.invoice) === null || _c === void 0 ? void 0 : _c.id]);
    React.useEffect(() => {
        var _a;
        (_a = props.onDisabledChange) === null || _a === void 0 ? void 0 : _a.call(props, !state.recipient);
    }, [props.onDisabledChange, state.recipient]);
    React.useImperativeHandle(ref, () => ({
        'submit': () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                if (!props.invoice) {
                    return false;
                }
                mutators.errors([]);
                let sent = false;
                if (!state.which_external) {
                    mutators.errors([{ 'field': 'recipient', 'message': 'Recipient is required' }]);
                    return false;
                }
                yield postObjectJson(`/merchants/${props.invoice.merchant.id}/invoices/${props.invoice.id}/send-for-approval/${state.which_external}`, {
                    'site': SITE,
                    'recipient': state.recipient,
                    'send_kind': props.sendKind,
                    'include_inspections': includeInspections,
                }, {
                    'error': (res) => __awaiter(void 0, void 0, void 0, function* () { return mutators.errors(yield res.json()); }),
                    'success': () => __awaiter(void 0, void 0, void 0, function* () {
                        var _a;
                        sent = true;
                        (_a = props.onSent) === null || _a === void 0 ? void 0 : _a.call(props);
                    }),
                });
                return sent;
            }
            catch (_a) {
                mutators.errors([{ 'field': '_', 'message': 'An error occured' }]);
                return false;
            }
        }),
    }), [props.invoice, props.onSent, props.sendKind, state.recipient, state.which_external, includeInspections]);
    const previewHref = `/external/${(_d = props.invoice) === null || _d === void 0 ? void 0 : _d.merchant.id}/${(_e = props.invoice) === null || _e === void 0 ? void 0 : _e.id}${includeInspections ? '?inspections=1' : ''}`;
    return _jsxs(_Fragment, { children: [_jsx(CSelect, { items: [['phone', 'Text'], ['email', 'Email']], valuef: (x) => x[0], titlef: (x) => x[1], value: state.which_external, onChange: (x) => mutators.which_external((x || 'phone')), children: "Delivery Method" }), inspections.length > 0 && _jsx("div", { className: "mb-3", children: _jsx(CSwitch, { value: includeInspections, onChange: setIncludeInspections, slotChecked: _jsx(_Fragment, { children: "Include inspection details" }), slotUnchecked: _jsx(_Fragment, { children: "Do not include inspection details" }) }) }), state.which_external === 'phone' && _jsxs(_Fragment, { children: [_jsx(CErrors, { errors: state.errors, mapping: { 'recipient': 'Phone' } }), _jsx(ContactPicker, { mode: "sms", contacts: ((_f = props.invoice) === null || _f === void 0 ? void 0 : _f.client.contacts) || [], value: state.recipient, onChange: mutators.recipient, children: "Phone" }), _jsx("div", { className: "text-center", children: _jsx("a", { target: "_blank", href: previewHref, children: "Preview Link" }) })] }), state.which_external === 'email' && _jsxs(_Fragment, { children: [_jsx(CErrors, { errors: state.errors, mapping: { 'recipient': 'Email' } }), _jsx(ContactPicker, { mode: "email", contacts: ((_g = props.invoice) === null || _g === void 0 ? void 0 : _g.client.contacts) || [], value: state.recipient, onChange: mutators.recipient, children: "Email" }), _jsx("div", { className: "text-center", children: _jsx("a", { target: "_blank", href: previewHref, children: "Preview Link" }) })] })] });
});
export default (props) => {
    const formRef = React.useRef(null);
    const [disabled, setDisabled] = React.useState(true);
    return _jsx(CModal, { visible: !!props.invoice, title: `Send Estimate - ${formatInvoiceNumber(props.invoice)}`, onClose: () => props.onClose(false), onSubmit: (complete) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const sent = yield ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.submit());
            complete();
            if (sent) {
                props.onClose(true);
            }
        }), action: "Send", disabled: disabled, children: _jsx(EstimateSendForm, { ref: formRef, invoice: props.invoice, sendKind: props.sendKind, onDisabledChange: setDisabled }) });
};
